"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useBranchCircuitStatus = void 0;
const react_1 = require("react");
const hooks_1 = require("../../hooks");
const types_1 = require("../../types");
const processAlertsByLocation_1 = require("../../utils/alerts/processAlertsByLocation");
const stableValue_1 = require("../../utils/stableValue");
const useStableList_1 = require("../useStableList");
/**
 * Severity-only on purpose, and safe to leave that way: this only ever sees
 * `BranchCircuitAlertTypes` (filtered below), which excludes SENSOR_INACTIVE.
 * So the offline coverage channel cannot reach here and does NOT need the
 * alertType-aware `alertToCardStatus` treatment. Don't "fix" it.
 */
const mapSeverityToStatus = (severity) => {
    switch (severity) {
        case types_1.SensorAlertSeverity.Critical:
        case types_1.SensorAlertSeverity.High:
            return types_1.SensorCardStatus.Error;
        case types_1.SensorAlertSeverity.Medium:
        case types_1.SensorAlertSeverity.Low:
            return types_1.SensorCardStatus.Poor;
        case types_1.SensorAlertSeverity.Info:
        default:
            return types_1.SensorCardStatus.Good;
    }
};
const useBranchCircuitStatus = ({ selectedHubId, sensors: providedSensors, }) => {
    const { sensors: fetchedSensors } = (0, hooks_1.useSensors)(providedSensors ? undefined : selectedHubId ?? undefined);
    const sensors = providedSensors ?? fetchedSensors;
    const { fetchSensorAlerts } = (0, hooks_1.useSensorAlerts)();
    const [alertsPerLocation, setAlertsPerLocation] = (0, react_1.useState)(undefined);
    // Only power monitors carry branch circuits and electrical alerts: an update to any other
    // household sensor no longer re-queries alerts or rebuilds the list.
    const powerSensors = (0, useStableList_1.useStableList)(sensors.filter((sensor) => sensor.type === types_1.SensorType.POWER));
    const branchCircuits = (0, react_1.useMemo)(() => {
        if (!alertsPerLocation) {
            return [];
        }
        const branchCircuits = powerSensors
            .filter((sensor) => sensor.branchCircuits && sensor.branchCircuits.length > 0)
            .flatMap((sensor) => sensor.branchCircuits);
        const uniqueLocations = new Set(branchCircuits.map((circuit) => circuit.location).filter(Boolean));
        const items = Object.entries(alertsPerLocation)
            .filter(([location]) => uniqueLocations.has(location))
            .map(([location, alerts]) => {
            // Filter alerts to only consider electrical alerts defined in BranchCircuitAlertTypes
            const electricalAlerts = alerts.filter(alert => types_1.BranchCircuitAlertTypes.includes(alert.alertType));
            // Only use electrical alerts for status determination - if no electrical alerts exist, branch is healthy
            const status = electricalAlerts.length > 0
                ? mapSeverityToStatus(electricalAlerts[0].severity)
                : types_1.SensorCardStatus.Good;
            // Find the sensor that has a branch circuit with this location
            const correspondingSensor = powerSensors.find((sensor) => sensor.branchCircuits?.some((circuit) => circuit.location === location));
            return {
                location,
                status,
                alert: electricalAlerts.length > 0 ? electricalAlerts[0] : null,
                lastReported: correspondingSensor?.lastReported ?? null,
            };
        });
        return items;
    }, [alertsPerLocation, powerSensors]);
    // Power monitors only: processAlertsByLocation fetched 50 alerts for every household sensor
    // one after another, and only power monitors' electrical alert types are ever used here.
    const hasSensors = sensors.length > 0;
    const fetchAlerts = async () => {
        if (!hasSensors) {
            return [];
        }
        // Sensors but no power monitor: nothing to fetch, and no longer loading either.
        const alerts = powerSensors.length === 0 ? {} : await (0, processAlertsByLocation_1.processAlertsByLocation)(powerSensors, fetchSensorAlerts);
        setAlertsPerLocation((prev) => (0, stableValue_1.stable)(prev, alerts));
    };
    (0, react_1.useEffect)(() => {
        fetchAlerts();
    }, [powerSensors, selectedHubId, hasSensors]);
    const loading = alertsPerLocation === undefined;
    return { branchCircuits, loading };
};
exports.useBranchCircuitStatus = useBranchCircuitStatus;
